import { ClockIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SelectDropdown } from "../SelectDropdown";
import { LoaderSpinner } from "../LoaderSpinner";
import {
    DemandPlanningLoadingKeys,
    clearForecastDemandPlanningHistory,
    getForecastDemandPlanningHistory,
} from "../../slices/demandPlanningSlice";
import { getUsersToSelect, UsersManagementLoadingKeys } from "../../slices/managementSlices/usersSettingsSlice";
import { useDebounce } from "../../helpers/useDebounce";

const ACTION_LABELS = {
    INSERT: "Created",
    UPDATE: "Updated",
    DELETE: "Deleted",
};

const getInitials = (name) => {
    const cleaned = String(name || "").trim();
    if (!cleaned) return "?";
    const parts = cleaned.split(/\s+/).filter(Boolean);
    const first = (parts[0] || "").slice(0, 1).toUpperCase();
    const second = (parts[1] || "").slice(0, 1).toUpperCase();
    return `${first}${second}` || first || "?";
};

export const DemandPlanningHistorySidebar = ({
    isOpen,
    demandPlanningId,
    demandPlanningYear,
    onClose,
}) => {
    const [actionUser, setActionUser] = useState("all_users");
    const [search, setSearch] = useState("");
    const currentSearchValue = useDebounce(search, 500);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [pageSize] = useState(25);
    const dispatch = useDispatch();

    const {
        usersToSelect,
        loadingKeys: usersLoadingKeys,
    } = useSelector((state) => state.usersSettings);
    const {
        loadingKeys: demandPlanningLoadingKeys,
        forecastDemandPlanningHistory,
        forecastDemandPlanningHistoryTotal,
        forecastDemandPlanningHistoryPage,
    } = useSelector((state) => state.demandPlanning);

    const usersToSelectIsLoading = () => usersLoadingKeys.includes(UsersManagementLoadingKeys.getUsersToSelect);
    const historyIsLoading = () =>
        demandPlanningLoadingKeys.includes(DemandPlanningLoadingKeys.getForecastDemandPlanningHistory);

    const canLoadMore =
        !!appliedFilters &&
        (forecastDemandPlanningHistory || []).length < Number(forecastDemandPlanningHistoryTotal || 0);

    useEffect(() => {
        if (isOpen) {
            dispatch(getUsersToSelect({}));
        }
    }, [isOpen, dispatch]);

    useEffect(() => {
        dispatch(clearForecastDemandPlanningHistory());
        setAppliedFilters(null);
    }, [demandPlanningId, demandPlanningYear, dispatch]);

    // Load the first page automatically whenever the panel opens for a plan.
    useEffect(() => {
        if (!isOpen || !demandPlanningId) return;
        const filters = { search: "", userId: "all_users" };
        setAppliedFilters(filters);
        dispatch(getForecastDemandPlanningHistory({
            demandPlanningId,
            forecastYear: demandPlanningYear,
            ...filters,
            page: 1,
            pageSize,
        }));
    }, [isOpen, demandPlanningId, demandPlanningYear, dispatch, pageSize]);

    const applyFilters = () => {
        const filters = {
            search: String(currentSearchValue || "").trim(),
            userId: actionUser,
        };
        setAppliedFilters(filters);
        dispatch(getForecastDemandPlanningHistory({
            demandPlanningId,
            forecastYear: demandPlanningYear,
            ...filters,
            page: 1,
            pageSize,
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-y-0 right-0 w-full max-w-xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col">
            <div className="p-6 pb-2 border-border border-b">
                <div className="flex flex-col mb-2 gap-4">
                    <div className="flex items-center justify-between">
                        <div className="flex flex-row gap-2 items-center">
                            <ClockIcon className="h-5 w-5 text-primary" />
                            <h2 className="text-lg font-semibold">Change Log</h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 h-8 rounded-md gap-1.5 px-3"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="space-y-4">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <input
                                placeholder="Search product name or SKU..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] pl-10"
                            />
                        </div>
                        <div className="flex gap-2">
                            <div className="w-full flex flex-row gap-2 items-center">
                                <span>User:</span>
                                <SelectDropdown
                                    options={[
                                        { label: "All Users", value: "all_users" },
                                        ...(usersToSelect || []),
                                    ]}
                                    value={actionUser}
                                    onChange={setActionUser}
                                    disabled={usersToSelectIsLoading()}
                                    selectClassname="bg-white py-1.5"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row gap-2 justify-end">
                        <button
                            type="button"
                            disabled={historyIsLoading() || !demandPlanningId}
                            onClick={applyFilters}
                            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 h-9 rounded-md gap-1.5 px-4 bg-primary text-white hover:opacity-90"
                        >
                            Apply filters
                        </button>
                    </div>
                </div>
            </div>
            <div className="flex-1 overflow-y-auto overflow-x-hidden relative">
                {historyIsLoading() && (
                    <div className="fixed inset-0 bg-black opacity-30 z-10">
                        <LoaderSpinner height="70" width="70" colorSpinner="green" border="5" />
                    </div>
                )}
                <div className="p-6 space-y-3">
                    {(forecastDemandPlanningHistory || []).length === 0 && !historyIsLoading() && (
                        <div className="text-sm text-gray-500 text-center py-8">
                            No changes recorded yet.
                        </div>
                    )}
                    {(forecastDemandPlanningHistory || []).map((item) => (
                        <div
                            key={`${item.entityType || "unknown"}-${item.id}`}
                            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-gray-900 truncate">
                                        {item.entityType === "product"
                                            ? `${item.productSku ? `${item.productSku} · ` : ""}${item.productName || "Product"}`
                                            : (item.planName || "Demand plan")}
                                    </div>
                                    <div className="mt-1 flex items-center gap-2">
                                        <div className="h-8 w-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-semibold text-gray-700">
                                            {getInitials(item.userName)}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-xs font-medium text-gray-800 truncate">
                                                {item.userName || "Unknown"}
                                            </div>
                                            <div className="text-[11px] text-gray-500">
                                                {item.changedDate}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <span className="shrink-0 inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                                    {ACTION_LABELS[item.action] || item.action}
                                </span>
                            </div>
                            <div className="mt-3 space-y-1">
                                {(item.changes || []).map((change) => (
                                    <div
                                        key={`${item.entityType || "unknown"}-${item.id}_${change.field}`}
                                        className="flex items-center justify-between gap-2 text-xs"
                                    >
                                        <span className="text-gray-700 shrink-0">{change.label}</span>
                                        {change.complex ? (
                                            <span className="font-medium text-gray-500 italic">updated</span>
                                        ) : (
                                            <div className="flex items-center gap-2 min-w-0 justify-end">
                                                <span className="font-medium text-gray-500 truncate">
                                                    {change.from}
                                                </span>
                                                <span className="text-gray-400">→</span>
                                                <span className="font-semibold text-gray-900 truncate">
                                                    {change.to}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {canLoadMore && (
                        <button
                            disabled={historyIsLoading()}
                            onClick={() => {
                                dispatch(getForecastDemandPlanningHistory({
                                    demandPlanningId,
                                    forecastYear: demandPlanningYear,
                                    ...(appliedFilters || {
                                        search: String(currentSearchValue || "").trim(),
                                        userId: actionUser,
                                    }),
                                    page: Number(forecastDemandPlanningHistoryPage || 1) + 1,
                                    pageSize,
                                }));
                            }}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Load more
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DemandPlanningHistorySidebar;
